import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Button,
  SearchBar,
  ToggleCheckbox,
} from "../../../components/tailwind";
import { Field, Fieldset } from "./types";
import { Text } from "../../../components/Text";
import { updateFieldset } from "../../../api/fieldsetApi";
import { RootState } from "../../../utils/store";

const fieldHasFieldsetField = (
  field: Field,
  fieldsetField: { key?: string; section_id?: number }
): boolean => {
  if (field.key && fieldsetField.key) return field.key === fieldsetField.key;
  if (field.section_id && fieldsetField.section_id)
    return field.section_id === fieldsetField.section_id;
  return false;
};

const fieldsetContainsField = (fieldset: Fieldset, field: Field): boolean =>
  fieldset.fields.some((f) => fieldHasFieldsetField(field, f));

const fieldPayload = (field: Field): { key?: string; section_id?: number } => {
  if (field.key) return { key: field.key };
  if (field.section_id) return { section_id: field.section_id };
  return {};
};

type Props = {
  field: Field;
  fieldsets: Fieldset[];
  refetchFieldsets: () => Promise<void>;
};

export const FieldSchemaToggleList: React.FC<Props> = ({
  field,
  fieldsets,
  refetchFieldsets,
}) => {
  const token = useSelector((state: RootState) => state.auth.token);

  const savableFieldsets = useMemo(
    () => fieldsets.filter((fs) => fs.id !== 0),
    [fieldsets]
  );

  const initialSelectedIds = useMemo(() => {
    const ids = new Set<number>();
    savableFieldsets.forEach((fs) => {
      if (fieldsetContainsField(fs, field)) ids.add(fs.id);
    });
    return ids;
  }, [savableFieldsets, field]);

  const [selectedIds, setSelectedIds] = useState<Set<number>>(
    initialSelectedIds
  );
  const [search, setSearch] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return (): void => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    setSelectedIds(initialSelectedIds);
  }, [initialSelectedIds]);

  const filteredFieldsets = useMemo(() => {
    if (!search) return savableFieldsets;
    const q = search.toLowerCase();
    return savableFieldsets.filter((fs) => fs.name.toLowerCase().includes(q));
  }, [savableFieldsets, search]);

  const toggleFieldset = (id: number, checked: boolean): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const onSave = async (): Promise<void> => {
    setIsSaving(true);
    try {
      const payload = fieldPayload(field);
      for (const fieldset of savableFieldsets) {
        const hasField = fieldsetContainsField(fieldset, field);
        const shouldHave = selectedIds.has(fieldset.id);
        if (hasField === shouldHave) continue;
        const newFields = shouldHave
          ? [...fieldset.fields, payload]
          : fieldset.fields.filter((f) => !fieldHasFieldsetField(field, f));
        await updateFieldset(token, fieldset.id, {
          name: fieldset.name,
          fields: newFields,
          use_as_default: fieldset.use_as_default,
        });
      }
      await refetchFieldsets();
    } finally {
      if (mounted.current) setIsSaving(false);
    }
  };

  return (
    <div
      className="tw-flex tw-flex-col tw-gap-4 tw-py-2"
      data-testid="field-schema-toggle-list"
    >
      <SearchBar
        value={search}
        onValueChange={setSearch}
        placeholder="Search schemas"
        data-testid="field-schema-toggle-search"
      />
      <ul
        className="tw-flex tw-flex-col tw-divide-y tw-divide-gray-100 tw-overflow-auto tw-pr-1"
        style={{
          maxHeight: "calc(100vh - 280px)",
          scrollbarGutter: "stable",
        }}
      >
        {filteredFieldsets.length === 0 && (
          <li
            className="tw-px-3 tw-py-3 tw-text-sm tw-text-gray-500"
            data-testid="field-schema-toggle-empty"
          >
            No schemas found
          </li>
        )}
        {filteredFieldsets.map((fs) => {
          const checked = selectedIds.has(fs.id);
          const checkboxId = `field-schema-toggle-${fs.id}`;
          return (
            <li
              key={fs.id}
              className="tw-flex tw-items-center tw-justify-between tw-py-3 tw-px-1"
            >
              <label
                htmlFor={checkboxId}
                className="tw-cursor-pointer tw-text-sm tw-font-medium"
              >
                {fs.name}
              </label>
              <ToggleCheckbox
                checkboxId={checkboxId}
                checked={checked}
                setChecked={(value): void => toggleFieldset(fs.id, value)}
                data-testid={`field-schema-toggle-${fs.name}`}
              />
            </li>
          );
        })}
      </ul>
      <div className="tw-flex tw-items-center tw-gap-2">
        <Button
          data-testid="fieldset-submit-form"
          variant="secondary"
          compact
          loading={isSaving}
          disabled={isSaving}
          onClick={onSave}
        >
          Save
        </Button>
        <Text size="tiny" color="grey" inline compact>
          {selectedIds.size} schema{selectedIds.size === 1 ? "" : "s"} on
        </Text>
      </div>
    </div>
  );
};
